import React, { useState, useEffect, useMemo } from 'react';
import {
    SpecDetail,
    SpecType,
    SpecDetailString,
    SpecDetailStringList,
    SpecTableContent,
    SpecStringContent,
    SpecStringListContent,
    SpecSymbolContent,
    SpecDetailTable,
} from '../types';
import LanguageTag from './LanguageTag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import SpecTextEdit from './SpecTextEdit';
import SpecTableEdit from './SpecTableEdit';
import { autoResizeTextArea } from '../utils/domUtils';

interface SpecEditFormProps {
    initialSpecs: SpecDetail[];
    onSpecsChange: (
        specs: SpecDetail[],
        isModified: boolean,
        hasEmptyFields: boolean,
        hasTableError: boolean,
    ) => void;
    // Prop to indicate if this form is for creating a single new spec (to hide "Add language" button)
    isCreatingNewSpec?: boolean;
}

const SpecEditForm: React.FC<SpecEditFormProps> = ({
    initialSpecs,
    onSpecsChange,
    isCreatingNewSpec = false,
}) => {
    const [editedSpecs, setEditedSpecs] = useState<SpecDetail[]>(() =>
        JSON.parse(JSON.stringify(initialSpecs)),
    );
    const [additionalInputs, setAdditionalInputs] = useState<{
        [key: number]: string[];
    }>({});
    const [isModified, setIsModified] = useState(false);
    const [hasTableValidationError, setHasTableValidationError] =
        useState<boolean>(false);

    useEffect(() => {
        const specsAreIdentical =
            JSON.stringify(initialSpecs) === JSON.stringify(editedSpecs);
        const hasAdditionalInputs = Object.keys(additionalInputs).length > 0;

        // The form is modified if the specs are different OR if there are additional inputs.
        const newIsModifiedValue = !specsAreIdentical || hasAdditionalInputs;

        // Only call setIsModified if the actual value of isModified needs to change.
        // This helps prevent infinite loops if dependencies change but the logical
        // outcome for isModified remains the same.
        if (isModified !== newIsModifiedValue) {
            setIsModified(newIsModifiedValue);
        }
        // isModified is included in the dependency array because we are reading its
        // current value to decide whether to update it. This is a standard pattern.
    }, [initialSpecs, editedSpecs, additionalInputs, isModified]);

    const allSpecsHaveLanguage = useMemo(() => {
        // Filter out table specs, as they don't have a language attribute relevant to this check.
        const relevantSpecs = editedSpecs.filter(
            (spec) => spec.spec_type !== SpecType.SPEC_TABLE,
        );
        // If there are no relevant specs (e.g., only table specs), consider this condition met.
        if (relevantSpecs.length === 0) return true;
        return relevantSpecs.every((spec) => spec.language !== null);
    }, [editedSpecs]);

    const allSpecsAreText = useMemo(() => {
        return editedSpecs.every(
            (spec) =>
                spec.spec_type === SpecType.SPEC_STRING ||
                spec.spec_type === SpecType.SPEC_STRING_LIST,
        );
    }, [editedSpecs]);

    const hasEmptyFields = useMemo(() => {
        const emptyPrimaryFields = editedSpecs.some((spec) => {
            if (spec.spec_type === SpecType.SPEC_STRING) {
                return (spec as SpecDetailString).content.text.trim() === '';
            } else if (spec.spec_type === SpecType.SPEC_STRING_LIST) {
                return (spec as SpecDetailStringList).content.text.some(
                    (item) => item.trim() === '',
                );
            }
            return false;
        });
        const emptyAdditionalFields = Object.values(additionalInputs).some(
            (inputs) => inputs.some((text) => text.trim() === ''),
        );
        return (
            emptyPrimaryFields ||
            emptyAdditionalFields ||
            hasTableValidationError
        );
    }, [editedSpecs, additionalInputs, hasTableValidationError]);

    useEffect(() => {
        // Consolidate specs with additional inputs before calling onSpecsChange
        const specsToReport = JSON.parse(JSON.stringify(editedSpecs));
        Object.entries(additionalInputs).forEach(([specIndex, inputs]) => {
            const index = Number(specIndex);
            const spec = specsToReport[index];
            if (!spec) return; // Should not happen if additionalInputs are managed correctly

            if (spec.spec_type === SpecType.SPEC_STRING) {
                if (inputs.length > 0) {
                    specsToReport[index] = {
                        ...spec,
                        spec_type: SpecType.SPEC_STRING_LIST,
                        content: {
                            text: [spec.content.text, ...inputs],
                            text_size: spec.content.text_size, // Keep original text_size
                        },
                    };
                }
            } else if (spec.spec_type === SpecType.SPEC_STRING_LIST) {
                if (inputs.length > 0) {
                    specsToReport[index] = {
                        ...spec,
                        content: {
                            ...spec.content,
                            text: [...spec.content.text, ...inputs],
                        },
                    };
                }
            }
        });
        onSpecsChange(
            specsToReport,
            isModified,
            hasEmptyFields,
            hasTableValidationError,
        );
    }, [
        editedSpecs,
        additionalInputs,
        isModified,
        hasEmptyFields,
        hasTableValidationError,
        onSpecsChange,
    ]);

    const createEmptyContent = (specType: SpecType) => {
        switch (specType) {
            case SpecType.SPEC_STRING:
                return { text: '', text_size: null } as SpecStringContent;
            case SpecType.SPEC_STRING_LIST:
                return { text: [''], text_size: null } as SpecStringListContent;
            case SpecType.SPEC_TABLE:
                return { headers: [], rows: [] } as SpecTableContent;
            case SpecType.SPEC_SYMBOL:
                return {
                    symbol_id: '',
                    symbol_height: null,
                } as SpecSymbolContent;
            default:
                return { text: '', text_size: null } as SpecStringContent;
        }
    };

    const addNewSpec = () => {
        if (!allSpecsHaveLanguage || editedSpecs.length === 0) return;
        const templateSpec = JSON.parse(JSON.stringify(editedSpecs[0]));
        const newSpec: SpecDetail = {
            ...templateSpec,
            content: createEmptyContent(templateSpec.spec_type),
        };
        setEditedSpecs((prev) => [...prev, newSpec]);
        // setIsModified(true); // Handled by useEffect that calls onSpecsChange
    };

    const handleStringChange = (
        index: number,
        newText: string,
        e: React.ChangeEvent<HTMLTextAreaElement> | null,
    ) => {
        setEditedSpecs((prev) => {
            const updated = [...prev];
            const spec = updated[index] as SpecDetailString;
            updated[index] = {
                ...spec,
                content: { ...spec.content, text: newText },
            };
            return updated;
        });
        if (e?.target) autoResizeTextArea(e.target);
        else {
            setTimeout(() => {
                document
                    .querySelectorAll<HTMLTextAreaElement>(
                        `.spec-edit-item:nth-child(${index + 1}) .spec-edit-textarea`,
                    )
                    .forEach(autoResizeTextArea);
            }, 0);
        }
    };

    const handleStringListChange = (
        specIndex: number,
        textIndex: number,
        newText: string,
        e: React.ChangeEvent<HTMLTextAreaElement> | null,
    ) => {
        setEditedSpecs((prev) => {
            const updated = [...prev];
            const spec = updated[specIndex] as SpecDetailStringList;
            const newTextArray = [...spec.content.text];
            newTextArray[textIndex] = newText;
            updated[specIndex] = {
                ...spec,
                content: { ...spec.content, text: newTextArray },
            };
            return updated;
        });
        if (e?.target) autoResizeTextArea(e.target);
        else {
            setTimeout(() => {
                document
                    .querySelectorAll<HTMLTextAreaElement>(
                        `.spec-edit-item:nth-child(${specIndex + 1}) .spec-edit-textarea`,
                    )
                    .forEach(autoResizeTextArea);
            }, 0);
        }
    };

    const addInputField = (index: number) => {
        setAdditionalInputs((prev) => ({
            ...prev,
            [index]: [...(prev[index] || []), ''],
        }));
    };

    const removeInputField = (specIndex: number, inputIndex: number) => {
        setAdditionalInputs((prev) => {
            const newInputs = { ...prev };
            if (newInputs[specIndex]) {
                newInputs[specIndex] = newInputs[specIndex].filter(
                    (_, idx) => idx !== inputIndex,
                );
                if (newInputs[specIndex].length === 0)
                    delete newInputs[specIndex];
            }
            return newInputs;
        });
    };

    const handleAdditionalInputChange = (
        specIndex: number,
        inputIndex: number,
        value: string,
        e: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        setAdditionalInputs((prev) => {
            const updated = { ...prev };
            if (updated[specIndex]) {
                updated[specIndex] = [...updated[specIndex]];
                updated[specIndex][inputIndex] = value;
            }
            return updated;
        });
        autoResizeTextArea(e.target);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            document
                .querySelectorAll('.spec-edit-textarea')
                .forEach((textarea) => {
                    autoResizeTextArea(textarea as HTMLTextAreaElement);
                });
        }, 10);
        return () => clearTimeout(timer);
    }, [editedSpecs, additionalInputs]);

    const removeStringListItem = (specIndex: number, textIndex: number) => {
        setEditedSpecs((prev) => {
            const updated = [...prev];
            const spec = updated[specIndex] as SpecDetailStringList;
            let newTextArray = [...spec.content.text];
            newTextArray.splice(textIndex, 1);
            // If last item is removed and it's the primary spec (not an additional input field being merged)
            // ensure there's at least one empty string if this spec is supposed to be a list.
            if (
                newTextArray.length === 0 &&
                spec.spec_type === SpecType.SPEC_STRING_LIST
            ) {
                newTextArray = [''];
            }
            updated[specIndex] = {
                ...spec,
                content: { ...spec.content, text: newTextArray },
            };
            return updated;
        });
        setTimeout(() => {
            document
                .querySelectorAll<HTMLTextAreaElement>(
                    `.spec-edit-item:nth-child(${specIndex + 1}) .spec-edit-textarea`,
                )
                .forEach(autoResizeTextArea);
        }, 0);
    };

    const removeSpec = (index: number) => {
        setEditedSpecs((prev) => {
            if (prev.length <= 1 && !isCreatingNewSpec) return prev; // Don't remove last item when editing existing
            if (prev.length === 0) return prev; // Cannot remove if empty
            const updated = [...prev];
            updated.splice(index, 1);
            // If all specs are removed (e.g. in create mode), reset additional inputs for the (now non-existent) 0th item
            if (updated.length === 0) {
                setAdditionalInputs({});
            }
            return updated;
        });
    };

    const handleLanguageChange = (
        index: number,
        newLanguage: string | null,
    ) => {
        if (
            newLanguage === null &&
            allSpecsHaveLanguage &&
            editedSpecs.length > 1 &&
            !isCreatingNewSpec
        )
            return;
        setEditedSpecs((prev) => {
            const updated = [...prev];
            if (updated[index]) {
                // Check if spec exists at index
                updated[index] = { ...updated[index], language: newLanguage };
            }
            return updated;
        });
    };

    const handleTableChange = (index: number, newContent: SpecTableContent) => {
        setEditedSpecs((prev) => {
            const updated = [...prev];
            if (
                updated[index] &&
                updated[index].spec_type === SpecType.SPEC_TABLE
            ) {
                // Check if spec exists and is a table
                updated[index] = { ...updated[index], content: newContent };
            }
            return updated;
        });
        // setIsModified is handled by onSpecsChange useEffect
        // setHasTableValidationError is also passed to SpecTableEdit directly if needed
    };

    const renderSpecContent = (spec: SpecDetail, index: number) => {
        const header = (
            <>
                {spec.spec_type !== SpecType.SPEC_TABLE && (
                    <div className="spec-content-header">
                        <div className="spec-language">
                            <LanguageTag
                                language={spec.language}
                                editable={true}
                                onLanguageChange={(lang) =>
                                    handleLanguageChange(index, lang)
                                }
                                disableNoLanguage={
                                    allSpecsHaveLanguage &&
                                    editedSpecs.filter(
                                        (s) =>
                                            s.spec_type !== SpecType.SPEC_TABLE,
                                    ).length > 1 && // Ensure we are comparing against relevant specs
                                    !isCreatingNewSpec
                                }
                            />
                        </div>
                        <div className="spec-actions">
                            {(spec.spec_type === SpecType.SPEC_STRING ||
                                spec.spec_type ===
                                    SpecType.SPEC_STRING_LIST) && (
                                <button
                                    className="icon-button"
                                    onClick={() => addInputField(index)}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            )}
                            {(!isCreatingNewSpec || editedSpecs.length > 1) && ( // Show remove if not creating new OR if there are multiple items (e.g. after adding language)
                                <button
                                    className="icon-button"
                                    onClick={() => removeSpec(index)}
                                    disabled={
                                        editedSpecs.length <= 1 &&
                                        !isCreatingNewSpec
                                    } // Protect last item when editing existing
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </>
        );

        switch (spec.spec_type) {
            case SpecType.SPEC_STRING:
            case SpecType.SPEC_STRING_LIST:
                return (
                    <>
                        {header}
                        <div className="spec-text">
                            <SpecTextEdit
                                spec={spec}
                                index={index}
                                additionalInputs={additionalInputs}
                                onStringChange={handleStringChange}
                                onStringListChange={handleStringListChange}
                                onAdditionalInputChange={
                                    handleAdditionalInputChange
                                }
                                onRemoveInputField={removeInputField}
                                onRemoveStringListItem={removeStringListItem}
                                onSetAdditionalInputs={setAdditionalInputs}
                            />
                        </div>
                    </>
                );
            case SpecType.SPEC_TABLE:
                return (
                    <>
                        {header}
                        {/* Table specs can also have language and be removed */}
                        <SpecTableEdit
                            spec={spec as SpecDetailTable}
                            onChange={(newContent) =>
                                handleTableChange(index, newContent)
                            }
                            setIsModified={setIsModified} // This component manages its own isModified for table
                            setHasValidationError={setHasTableValidationError}
                        />
                    </>
                );
            case SpecType.SPEC_SYMBOL:
                return (
                    <>
                        {header}
                        <div className="spec-edit-placeholder">
                            <p className="placeholder-text">
                                Symbol editing functionality coming soon...
                            </p>
                        </div>
                    </>
                );
            default:
                return (
                    <>
                        {header}
                        <div className="spec-edit-placeholder">
                            <p className="placeholder-text">
                                Unsupported spec type
                            </p>
                        </div>
                    </>
                );
        }
    };

    return (
        <>
            <div className="spec-edit-form">
                {editedSpecs.map((spec, index) => (
                    <div
                        key={`${spec.name}-${index}`}
                        className="spec-edit-item"
                    >
                        {' '}
                        {/* Ensure unique key */}
                        <div className="spec-content">
                            {renderSpecContent(spec, index)}
                        </div>
                    </div>
                ))}
            </div>
            {!isCreatingNewSpec && allSpecsHaveLanguage && allSpecsAreText && (
                <button className="add-language-btn" onClick={addNewSpec}>
                    <FontAwesomeIcon icon={faPlus} /> Add different language
                </button>
            )}
        </>
    );
};

export default SpecEditForm;
